import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { AideFieldValues, aideFormSchema } from "../lib/schemas/aide-form-schema";
import axiosClient from "../lib/axiosClient";
import { ksMotifsSupportQK, ksUserQK } from "../lib/query-keys";
import { findUser } from "../lib/queries/user-queries";
import { findAllMotifsSupport } from "../lib/queries/motifs-support-queries";
import { Alert } from "../components/ui/alert";
import Title from "../components/ui/Title";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

const AidePage = () => {
  const { data: user } = useQuery({
    queryKey: [ksUserQK],
    queryFn: findUser,
  });
  const { data: motifs } = useQuery({
    queryKey: [ksMotifsSupportQK],
    queryFn: findAllMotifsSupport,
  });
  const [success, setSuccess] = useState(false);
  const form = useForm<AideFieldValues>({
    resolver: zodResolver(aideFormSchema),
    defaultValues: { message: "", motifId: "" },
  });

  const { mutate: envoyer, isPending } = useMutation({
    mutationFn: async (data: AideFieldValues) => {
      if (!user) return;
      await axiosClient.post("/tickets-support", {
        motifId: data.motifId,
        message: data.message,
        userId: user.id,
      });
    },
    onSuccess: () => {
      setSuccess(true);
      toast.success("Message envoyé");
    },
    onError: (err: any) => {
      console.error(err.response?.data || err);
      toast.error(`Une erreur s'est produite`);
    },
  });

  return (
    <div>
      <Title className='mb-6'>Nous contacter</Title>
      {!!motifs && !!user ? (
        <>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((data) => envoyer(data))} className='flex flex-col gap-4'>
              {/* Motif */}
              <FormField
                control={form.control}
                name='motifId'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Veuillez préciser le motif de votre demande</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className='max-w-[480px]'>
                          <SelectValue placeholder='Sélectionnez un motif' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {motifs.map((mot) => (
                          <SelectItem key={mot.id} value={mot.id}>
                            {mot.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Message */}
              <FormField
                control={form.control}
                name='message'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Veuillez détailler votre demande</FormLabel>
                    <FormControl>
                      <Textarea rows={8} placeholder='Votre message...' {...field} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              {!success && (
                <div className='mt-3'>
                  <Button loading={isPending} type='submit'>
                    Envoyer le message
                  </Button>
                </div>
              )}
            </form>
          </Form>
          {success && (
            <Alert variant={"default"} className='bg-green-100 mt-4'>
              Votre message a bien été envoyé ! Vous recevrez une réponse par email rapidement.
            </Alert>
          )}
        </>
      ) : (
        <>
          <Skeleton className='w-full h-24 mb-4' />
          <Skeleton className='w-full h-48' />
        </>
      )}
    </div>
  );
};

export default AidePage;
